import { createSelector } from '@ngrx/store';
import { PopularFilter } from '@pages/travel/hotel/data-models';
import { ActivityFilter } from '@shared/models/filter.model';
import { HotelSubBookingData, RoomRequest } from '@shared/models/hotel-search-payload.model';
import { SELECTED_HOTEL_LOCAL_STORAGE_KEY } from 'app.config';
import * as fromReducers from '../reducers';
import * as fromHotelSearch from '../reducers/hotel-search.reducers';
import { HotelSearchInitialState } from '../reducers/hotel-search.reducers';
import { getHotelAmenitiesFeatureState } from './hotel-amenities.selectors';

export const getAllRecomendedHotelsState = createSelector(
  fromReducers.getTravelSearchState,
  (state: fromReducers.TravelSearchState) => state.hotelSearch.recommendedHotels
);

export const getHotelSearchFeatureState = createSelector(
  fromReducers.getTravelSearchState,
  (state: fromReducers.TravelSearchState) => state.hotelSearch
);

export const getHotelSearchResultsState = createSelector(
  fromReducers.getTravelSearchState,
  (state: fromReducers.TravelSearchState) => state.hotelSearch.hotelSearchResults
);

// new selectors
export const getHotelSearchData = createSelector(getHotelSearchFeatureState, fromHotelSearch.getSearchData);
export const getHotelSearchResultsData = createSelector(
  getHotelSearchFeatureState,
  fromHotelSearch.getSearchResultsData
);
export const getVisibleHotelsData = createSelector(getHotelSearchFeatureState, fromHotelSearch.getVisibleHotels);
export const getHotelSearchResultsRetrieved = createSelector(
  getHotelSearchFeatureState,
  fromHotelSearch.getHotelSearchResultsLoaded
);
export const getHotelSearchResultsLoading = createSelector(
  getHotelSearchFeatureState,
  fromHotelSearch.getHotelSearchResultsLoading
);
export const getSelectedHotel = createSelector(getHotelSearchFeatureState, (state: HotelSearchInitialState) => {
  return state.selectedHotel || JSON.parse(localStorage.getItem(SELECTED_HOTEL_LOCAL_STORAGE_KEY));
});

export const getHotelNarrowSearch = createSelector(getHotelSearchFeatureState, (state: HotelSearchInitialState) => {
  return { hotelSearchValue: state.hotel_search_by_name, hotelIsSelected: state.hotel_is_selected };
});

export const getHotelSubBookingData = createSelector(getHotelSearchFeatureState, (state: HotelSearchInitialState) => {
  let startDate = new Date(state.hotelSearch.start_date_full).toISOString();
  let endDate = new Date(state.hotelSearch.end_date_full).toISOString();

  startDate = startDate.split('T')[0];
  endDate = endDate.split('T')[0];

  const roomRequests = state.hotelSearch.room_requests;

  return {
    hotel_ivector_connect_id: state?.selectedHotel.product_id?.toString(),
    start_date: startDate,
    end_date: endDate,
    search_reference_id: state?.hotelSearchResults?.search_reference_id,
    room_requests: roomRequests.map((roomRequest: RoomRequest, i: number) => ({
      ...roomRequest,
      room_type: state.selectedRoom[i]?.room_type,
      room_booking_token: state?.selectedRoom[i]?.booking_token || state?.selectedRoom[0]?.booking_token,
    })),
    supplier: state.selectedRoom[0]?.supplier,
    search_result_booking_token: state?.selectedHotel.booking_token,
    room_type: state?.selectedRoom[0]?.room_type,
  } as HotelSubBookingData;
});

export const getHotelSearchResults = (skipFilter?: string) =>
  createSelector(getHotelSearchResultsState, (state) => getFilteredHotelSearchResults(state, skipFilter));

export const getHotelFilter = createSelector(getHotelSearchFeatureState, (state) => {
  return state.hotelSearchResults.hotelFilterForm;
});

export const getDefaultHotelFilterForm = createSelector(
  getHotelSearchResultsState,
  getHotelAmenitiesFeatureState,
  (state, amenities) => {
    return {
      ...state.defaultFilterForm,
      amenities: state.defaultFilterForm.amenities.map((amenityFilter: ActivityFilter<number>) => {
        return {
          ...amenityFilter,
          name: amenities.find((amenity) => amenity.id === amenityFilter.value)?.title,
        };
      }),
    };
  }
);

export const getHotelNameFilter = createSelector(getHotelFilter, (state) => state.hotelName);

export const getSelectedHotelPriceRange = createSelector(getHotelSearchResultsState, (state) => {
  return state.hotelFilterForm.priceRange;
});

export const getSelectableHotelPriceRange = createSelector(getDefaultHotelFilterForm, (form) => {
  return form.priceRange;
});

export const getHotelRatingFilter = createSelector(
  getHotelFilter,
  getHotelSearchResults('starRating'),
  (form, hotelSearchResults) => {
    return form.starRating.map((rating: ActivityFilter) => {
      const enabled = hotelSearchResults.filteredMappedSortedResults.some((r) => Math.floor(r.rating) === rating.value);
      return {
        ...rating,
        enabled,
      } as ActivityFilter;
    });
  }
);

export const getHotelAmenityFilter = createSelector(
  getHotelFilter,
  getHotelSearchResults(''),
  getHotelAmenitiesFeatureState,
  (form, hotelSearchResults, amenities) => {
    return form.amenities.map((amenityFilter: ActivityFilter<number>) => {
      const enabled = hotelSearchResults.filteredMappedSortedResults.some((r) =>
        r.attributeIds.includes(amenityFilter.value)
      );
      const amenityName = amenities.find((amenity) => amenity.id === amenityFilter.value)?.title;
      return {
        ...amenityFilter,
        enabled,
        name: amenityName,
      } as ActivityFilter;
    });
  }
);

export const getHotelPopularFiltersFilter = createSelector(
  getHotelFilter,
  getHotelSearchResults(''),
  (form, hotelSearchResults) => {
    return form.popularFilters.map((popularFilter: PopularFilter) => {
      let enabled = false;
      if (popularFilter.popularFilterType === 'benefits') {
        enabled = hotelSearchResults.filteredMappedSortedResults.some((r) => r.isBenefitsHotel);
      } else if (popularFilter.popularFilterType === 'adyenInstalments') {
        enabled = hotelSearchResults.filteredMappedSortedResults.some((r) => r.adyenInstalmentsAvailable);
      }
      return {
        ...popularFilter,
        enabled,
      } as PopularFilter;
    });
  }
);

export const getSelectedHotelFilterCountBreakDown = createSelector(
  getHotelFilter,
  getSelectableHotelPriceRange,
  (filter, defaultPriceRange) => {
    return {
      hotelName: filter.hotelName ? 1 : 0,
      priceRange:
        filter.priceRange[0] !== defaultPriceRange[0] || filter.priceRange[1] !== defaultPriceRange[1] ? 1 : 0,
      starRating: filter.starRating.filter((star) => star.selected).length,
      amenities: filter.amenities.filter((amenity) => amenity.selected).length,
      isBenefitsHotel: filter.popularFilters.filter((popularFilter) => popularFilter.selected).length,
    };
  }
);

export const getTotalNumberOfAppliedFilters = createSelector(
  getHotelFilter,
  getHotelRatingFilter,
  getSelectableHotelPriceRange,
  getHotelAmenityFilter,
  getHotelPopularFiltersFilter,
  (filter, ratingFilter, defaultPriceRange, amenities, popularFilters) => {
    let total = 0;

    if (filter.hotelName) {
      total++;
    }
    if (filter.priceRange[0] !== defaultPriceRange[0] || filter.priceRange[1] !== defaultPriceRange[1]) {
      total++;
    }
    const ratingCount = ratingFilter?.filter((star) => star.selected).length;
    if (ratingCount) {
      total += ratingCount;
    }
    const amenitiesCount = amenities?.filter((amenity) => amenity.selected).length;
    if (amenitiesCount) {
      total += amenitiesCount;
    }
    const popularFiltersCount = popularFilters.filter((popularFilter) => popularFilter.selected).length;
    if (popularFiltersCount) {
      total += popularFiltersCount;
    }

    return total;
  }
);

export function getFilteredHotelSearchResults(res: fromHotelSearch.HotelSearchResult, skipFilter?: string) {
  if (!res) {
    return null;
  }

  let filteredResults = res.mappedSortedResults;

  if (skipFilter !== 'hotelName' && res.hotelFilterForm.hotelName) {
    filteredResults = filteredResults.filter(
      (hotel) => hotel.hotelName.toLowerCase() === res.hotelFilterForm.hotelName.toLowerCase()
    );
  }

  if (skipFilter !== 'priceRange' && res.hotelFilterForm.priceRange) {
    filteredResults = filteredResults.filter(
      (hotel) =>
        hotel.fromPrice >= res.hotelFilterForm.priceRange[0] && hotel.fromPrice <= res.hotelFilterForm.priceRange[1]
    );
  }

  if (skipFilter !== 'starRating' && res.hotelFilterForm.starRating.some((rating) => rating.selected)) {
    filteredResults = filteredResults.filter((hotel) =>
      res.hotelFilterForm.starRating.some((rating) => rating.selected && rating.value === Math.floor(hotel.rating))
    );
  }

  if (skipFilter !== 'amenities' && res.hotelFilterForm.amenities.some((amenity) => amenity.selected)) {
    const selectedAmenityFilters = res.hotelFilterForm.amenities.filter((amenity) => amenity.selected);
    filteredResults = filteredResults.filter((hotel) =>
      selectedAmenityFilters.every((amenity: ActivityFilter<number>) => hotel.attributeIds.includes(amenity.value))
    );
  }

  if (
    skipFilter !== 'popularFilters' &&
    res.hotelFilterForm.popularFilters.some((popularFilter) => popularFilter.selected)
  ) {
    const benefitsFilterSelected = res.hotelFilterForm.popularFilters.some(
      (popularFilter) => popularFilter.selected && popularFilter.popularFilterType === 'benefits'
    );
    const adyenInstalmentsFilterSelected = res.hotelFilterForm.popularFilters.some(
      (popularFilter) => popularFilter.selected && popularFilter.popularFilterType === 'adyenInstalments'
    );

    if (benefitsFilterSelected) {
      filteredResults = filteredResults.filter((hotel) => hotel.isBenefitsHotel);
    }

    if (adyenInstalmentsFilterSelected) {
      filteredResults = filteredResults.filter((hotel) => hotel.adyenInstalmentsAvailable);
    }
  }

  return {
    ...res,
    filteredMappedSortedResults: filteredResults,
  };
}
